import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {EnvironmentVariableService} from 'app/core/services/environment-variable.service';
import {RoleService} from 'app/core/services/role.service';
import {DiaryService} from 'app/diary/diary.service';
import {DepartureProperty} from 'app/diary/properties/departure-properties';
import {DepartureReasonCodes, ReasonCode, ReasonCodeService} from 'app/diary/reason-codes/reason-codes.service';
import {WorkflowStates} from 'app/workflow/workflow-states';
import {BaseTransitionModalComponent} from '../base-transition-modal/base-transition-modal.component';
import {TransitionInputData} from '../transition-input-data';
import {TransitionOutputData} from '../transition-output-data';

@Component({
  selector: 'das-reason-code-selection-modal',
  templateUrl: './reason-code-selection-modal.component.html',
  styleUrls: [
    './reason-code-selection-modal.component.scss',
    '../base-transition-modal/base-transition-modal.component.scss',
  ],
})
export class ReasonCodeSelectionModalComponent extends BaseTransitionModalComponent {
  public predeterminedNote: string =
    'Note: This departure has been pre-determined by the Project Manager. By ' +
    'confirming your recommendation, you will determine the outcome of this ' +
    'departure application and notify all parties involved with this departure application.';
  public recommendationNote: string = null;
  public recommendedReasonCodes: ReasonCode[];

  constructor(
    public dialogRef: MatDialogRef<ReasonCodeSelectionModalComponent, TransitionOutputData>,
    @Inject(MAT_DIALOG_DATA) public data: TransitionInputData,
    roleService: RoleService,
    diaryService: DiaryService,
    reasonCodeService: ReasonCodeService,
    environmentVariableService: EnvironmentVariableService
  ) {
    super(dialogRef, data, roleService, diaryService, reasonCodeService, environmentVariableService);
    this.isRecommendation = false;

    this.populateRecommendedReasonCodes();

    if (this.data.options.to === WorkflowStates.REJECTED) {
      // Any to DETERMINED (not recommendation)
      this.data.description =
        'You do not support this departure application and you wish to reject it. ' +
        'Before confirming this, please consider whether requesting rework of the submission would be more ' +
        'appropriate. If you still wish to reject it, please indicate your reason(s) for rejection with Reason Codes.';
    } else if (this.data.options.to === WorkflowStates.PM_DETERMINATION) {
      // AS to PM recommendation
      this.data.description =
        'You are recommending that this departure ' +
        'is rejected. Before confirming this recommendation please consider ' +
        'whether requesting rework of the submission would be more appropriate. ' +
        'If you still wish to reject it, please indicate your reason(s) for rejection ' +
        `with Reason Codes${this.getPassingToPMNote()}.`;
      this.isRecommendation = !this.data.options.predetermined;
    } else if (this.data.options.to === null) {
      // TS to AS recommendation
      this.data.description =
        'You are recommending to the Authorising ' +
        'Signatory that this departure is rejected. Before confirming this ' +
        'recommendation please consider whether requesting rework of the ' +
        'submission would be more appropriate. If you still wish to recommend ' +
        'a rejection, please indicate your reason(s) for rejection with ' +
        'Reason Codes; these will be shown to the Authorising Signatory ' +
        'as your recommended Reason Codes.';
      this.isRecommendation = true;
    }
  }

  public populateRecommendedReasonCodes(): void {
    this.diaryService.getLatestReasonCodes(this.data.departureId).then((res: DepartureReasonCodes) => {
      if (res && res.recommendation) {
        this.reasonCodeService.get(...res.reasonCodes).then((reasonCodes: ReasonCode[]) => {
          this.recommendedReasonCodes = reasonCodes;
          this.setRecommendationNote();
        });
      } else {
        this.recommendedReasonCodes = null;
      }
    });
  }

  private setRecommendationNote(): void {
    switch (this.data.options.to) {
      case WorkflowStates.PM_DETERMINATION:
        this.recommendationNote =
          'The Technical Specialist(s) have recommended that this ' +
          'departure is rejected, their recommended Reason Codes have been prepopulated ' +
          'below, they are also shown in the diary.';
        break;
      case null:
        this.recommendationNote =
          'Another Technical Specialist has recommended that this ' +
          'departure is rejected, their recommended Reason Codes have been prepopulated ' +
          'below, they are also shown in the diary. Please review and discuss any changes ' +
          'with the other Technical Specialist(s).';
        break;
      case WorkflowStates.REJECTED:
        this.recommendationNote =
          'The Reason Codes have been prepopulated with the recommended Reason ' +
          'Codes from the Technical Specialist(s) and / or Authorising Signatory. Please review, amend if ' +
          'deemed appropriate and confirm the rejection.';
        break;
      case WorkflowStates.REWORK_REQUIRED:
        this.recommendationNote =
          'The Reason Codes have been prepopulated with the recommended ' +
          'Reason Codes from the Technical Specialist(s). Please review, ' +
          'amend if deemed appropriate and confirm the rework.';
        break;
      default:
        break;
    }
  }

  public skippingPmDetermination(): boolean {
    return this.data.options.predetermined && this.data.options.to === WorkflowStates.PM_DETERMINATION;
  }

  public hasRecommendation(): boolean {
    return this.recommendedReasonCodes && this.recommendedReasonCodes.length > 0;
  }

  public getPassingToPMNote(): string {
    return this.data.options.predetermined
      ? ''
      : '; these will be shown to the Project Manager as your recommended Reason Codes';
  }

  public hasWarning(): boolean {
    return this.isAgainstRecommendation() && super.hasWarning();
  }

  private isAgainstRecommendation(): boolean {
    return this.latestProperties &&
      this.latestProperties.properties &&
      this.latestProperties.properties[DepartureProperty.SPECIALIST_RECOMMENDATION] &&
      this.data &&
      this.data.options &&
      this.data.options.to
      ? this.latestProperties.properties[DepartureProperty.SPECIALIST_RECOMMENDATION] !== this.data.options.to
      : false;
  }

  public setReasonCodes(selectedCodes: string[]) {
    this.reasonCodes = selectedCodes;
  }
}
